import {EMPTY_DISPLAY} from './formatFrameworkFieldValue';

export type HtmlInsertResult =
  | {kind: 'html-block'; htmlContent: string}
  | {kind: 'paragraph'; text: string};

const HTML_TAG_RE = /<\/?[a-z][\s\S]*?>/i;

/** Rich HTML from Quill / WYSIWYG (used when answer.type was not normalized to `html`). */
const RICH_HTML_CONTENT_RE =
  /<(p|h[1-6]|ul|ol|li|blockquote|pre|div|table|a|strong|em|span|br)[\s/>]/i;

export const resolveSidebarAnswerType = (
  type?: string,
  fieldType?: string
): string => {
  const t = String(type ?? '').trim();
  const ft = String(fieldType ?? '').trim();
  if (t === 'html' || t === 'wysiwyg' || ft === 'wysiwyg') return 'html';

  return t || 'text';
};

export const isHtmlAnswerContent = (content: any): boolean => {
  if (!content) return false;

  const t = resolveSidebarAnswerType(content.type, content.fieldType);
  if (t === 'html') return true;

  const raw = content.value;
  if (typeof raw !== 'string') return false;
  const trimmed = raw.trim();
  if (!trimmed || !HTML_TAG_RE.test(trimmed)) return false;

  return RICH_HTML_CONTENT_RE.test(trimmed);
};

/** Strip tags for empty detection / sidebar preview only — does not mutate stored HTML. */
export const htmlToPlainTextPreview = (
  html: string,
  maxLength = 200
): string => {
  if (!html || typeof html !== 'string') return '';

  const plain = html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!plain) return EMPTY_DISPLAY;
  if (plain.length <= maxLength) return plain;

  return `${plain.slice(0, maxLength)}…`;
};

const hasVisibleHtmlContent = (html: string): boolean => {
  const trimmed = html.trim();
  if (!trimmed) return false;

  if (/<img\s/i.test(trimmed)) return true;

  const plain = html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return plain.length > 0;
};

const coerceHtmlString = (raw: unknown): string => {
  if (raw === undefined || raw === null) return '';
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object') return '';

  return String(raw);
};

export function prepareFrameworkHtmlForInsert(
  raw: unknown
): HtmlInsertResult {
  const html = coerceHtmlString(raw);

  if (!html.trim() || !hasVisibleHtmlContent(html)) {
    return {kind: 'paragraph', text: EMPTY_DISPLAY};
  }

  if (!RICH_HTML_CONTENT_RE.test(html) && !HTML_TAG_RE.test(html)) {
    return {kind: 'paragraph', text: html.trim()};
  }

  return {kind: 'html-block', htmlContent: html};
}

/** Push html-block or fallback paragraph nodes for a sidebar HTML answer. */
export function appendHtmlAnswerNodes(
  nodes: any[],
  rawValue: unknown,
  prefix = ''
): void {
  if (prefix) {
    nodes.push({
      type: 'paragraph',
      source: 'sidebar',
      children: [{text: prefix, bold: true}]
    });
  }

  const htmlInsert = prepareFrameworkHtmlForInsert(rawValue);
  if (htmlInsert.kind === 'html-block') {
    nodes.push({
      type: 'html-block',
      source: 'sidebar',
      htmlContent: htmlInsert.htmlContent,
      children: [{text: ''}]
    });
  } else {
    nodes.push({
      type: 'paragraph',
      source: 'sidebar',
      children: [{text: htmlInsert.text, color: '#999'}]
    });
  }

  nodes.push({
    type: 'paragraph',
    source: 'sidebar',
    children: [{text: ''}]
  });
}
